import * as React from 'react'
import { useContext, useMemo, useState } from 'react'
import {
    bundleIcon,
    CalendarMonthRegular,
    CalendarRegular,
    CopyArrowRightFilled,
    CopyArrowRightRegular,
    CopySelectFilled,
    CopySelectRegular,
    DeleteFilled,
    DeleteRegular,
} from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { Button, Menu, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, mergeClasses, Tooltip } from '@fluentui/react-components'
import { EmailOverviewProps, ExtendedEmailOverviewDto } from './FileEmailOverview'
import { useOverviewStyles } from '../../../../pages/BaseOverview/BaseOverview'
import { TimeFilterType } from '../../Time/BaseTimeOverview'
import { generateFromDate } from '../../../../pages/BaseOverview/BaseOverviewFilter'
import { useFormikContext } from 'formik'
import HammockDialog from '../../../components/Dialog/HammockDialog'
import { Destination } from '../../../../context/AxiosContext'
import HammockFormDialog from '../../../components/Dialog/HammockFormDialog'
import MutateMessageDialogBody, {
    getMutateMessageDialogBodySchema,
    MutateMessageDialogBodyProps,
    MutateMessageDialogBodyValues,
} from '../../../components/Dialog/Message/MutateMessageDialogBody'
import { EmailProcessingAction } from '../../../../components/Dialog/ProcessEmail/ProcessEmailForm'
import useUser from '../../../../hooks/useUser'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import DeleteMessageDialogBody, { DeleteMessageDialogBodyProps } from '../../../components/Dialog/Message/DeleteMessageDialogBody'

const CopyIcon = bundleIcon(CopySelectFilled, CopySelectRegular)
const MoveIcon = bundleIcon(CopyArrowRightFilled, CopyArrowRightRegular)
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular)

export type EmailHeaderFilterTagsOverviewProps = {
    selectedItems: ExtendedEmailOverviewDto[]
    reload: () => void
}

const filterTagItems: TimeFilterType[] = [
    {
        from: generateFromDate('lastquarter'),
        tag: 'lastquarter',
        icon: <CalendarMonthRegular />,
    },
    {
        from: generateFromDate('lastyear'),
        tag: 'lastyear',
        icon: <CalendarMonthRegular />,
        title: 'email.filing.filter.interval.year',
    },
    {
        from: generateFromDate('all'),
        tag: 'all',
        icon: <CalendarRegular />,
    },
]

export default function FileEmailHeaderFilterTags(props: EmailOverviewProps & EmailHeaderFilterTagsOverviewProps) {
    const { values, setValues } = useFormikContext<TimeFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const { host: currentHost } = useContext(AuthorizationContext)
    const { currentEmail } = useUser()

    const [openCopyMessage, setOpenCopyMessage] = useState(false)
    const [openMoveMessage, setOpenMoveMessage] = useState(false)
    const [openDeleteMessage, setOpenDeleteMessage] = useState(false)

    const checkedValues = useMemo<Record<string, string[]>>(
        () => ({
            tag: values.tag ? [`${values.tag}`] : [],
        }),
        [values.tag],
    )

    return (
        <div className={overviewStyles.flexGapXS}>
            <Menu positioning={{ autoSize: true }} checkedValues={checkedValues}>
                <MenuTrigger disableButtonEnhancement>
                    <Tooltip withArrow relationship={'label'} content={translate('email.filing.filter.interval.tooltip')}>
                        <Button
                            appearance={'secondary'}
                            className={mergeClasses(overviewStyles.filterTagButton, values.tag && overviewStyles.filterTagButtonSelected)}
                            icon={!values.tag ? <CalendarRegular /> : undefined}
                        >
                            {values.tag && translate(`email.filing.filter.interval.${values.tag}`)}
                        </Button>
                    </Tooltip>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        {filterTagItems.map((tag, index) => (
                            <MenuItemRadio
                                key={index}
                                name={'tag'}
                                value={tag.tag}
                                onClick={() =>
                                    setValues({
                                        ...props.defaultFilterValues,
                                        ...tag,
                                    })
                                }
                            >
                                {translate(`email.filing.filter.interval.${tag.tag}`)}
                            </MenuItemRadio>
                        ))}
                    </MenuList>
                </MenuPopover>
            </Menu>

            <Tooltip withArrow relationship={'label'} content={translate('action.copy')}>
                <Button
                    icon={<CopyIcon />}
                    appearance={'secondary'}
                    disabled={props.selectedItems?.length === 0}
                    onClick={() => setOpenCopyMessage(true)}
                />
            </Tooltip>
            <Tooltip withArrow relationship={'label'} content={translate('action.move')}>
                <Button
                    icon={<MoveIcon />}
                    appearance={'secondary'}
                    disabled={props.selectedItems?.length === 0}
                    onClick={() => setOpenMoveMessage(true)}
                />
            </Tooltip>
            <Tooltip withArrow relationship={'label'} content={translate('base.delete.submit')}>
                <Button
                    icon={<DeleteIcon />}
                    appearance={'secondary'}
                    disabled={props.selectedItems?.length === 0}
                    onClick={() => setOpenDeleteMessage(true)}
                />
            </Tooltip>
            <HammockFormDialog<MutateMessageDialogBodyValues, MutateMessageDialogBodyProps>
                open={openCopyMessage}
                setOpen={setOpenCopyMessage}
                dialogFormProps={() => ({
                    initialValues: {
                        references: [props.file?.reference],
                    },
                    onSubmit: () => {},
                    validationSchema: getMutateMessageDialogBodySchema(translate),
                })}
                dialogFormBody={MutateMessageDialogBody}
                dialogFormBodyProps={() => ({
                    items: props.selectedItems,
                    action: EmailProcessingAction.Copy,
                    destination: Destination.ReplicationEmail,
                    userEmail: currentHost.ReplicationMail,
                    notificationEmail: currentEmail,
                    file: props.file,
                    callback: () => props.reload(),
                })}
            />
            <HammockFormDialog<MutateMessageDialogBodyValues, MutateMessageDialogBodyProps>
                open={openMoveMessage}
                setOpen={setOpenMoveMessage}
                dialogFormProps={() => ({
                    initialValues: {
                        references: [],
                    },
                    onSubmit: () => {},
                    validationSchema: getMutateMessageDialogBodySchema(translate),
                })}
                dialogFormBody={MutateMessageDialogBody}
                dialogFormBodyProps={() => ({
                    items: props.selectedItems,
                    action: EmailProcessingAction.Move,
                    destination: Destination.ReplicationEmail,
                    userEmail: currentHost.ReplicationMail,
                    notificationEmail: currentEmail,
                    callback: () => props.reload(),
                })}
            />
            <HammockDialog<DeleteMessageDialogBodyProps>
                open={openDeleteMessage}
                setOpen={setOpenDeleteMessage}
                dialogBody={DeleteMessageDialogBody}
                dialogBodyProps={() => ({
                    items: props.selectedItems,
                    destination: Destination.ReplicationEmail,
                    userEmail: currentHost.ReplicationMail,
                    notificationEmail: currentEmail,
                    file: props.file,
                    callback: () => props.reload(),
                })}
            />
        </div>
    )
}
